/**
 * Onboarding page for new users after Clerk registration
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToggleEmailNotificationsMutation } from '../services/api';
import { useUser } from '@clerk/clerk-react';

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [subscribeToEmails, setSubscribeToEmails] = useState(true); // Default: opt-in
  const [toggleEmailNotifications, { isLoading }] = useToggleEmailNotificationsMutation();

  const handleContinue = async () => {
    try {
      // Set email preferences
      await toggleEmailNotifications(subscribeToEmails).unwrap();
      // Redirect to dashboard/home
      navigate('/');
    } catch (error) {
      console.error('Failed to set email preferences:', error);
      // Continue anyway, user can change preferences later
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-2xl w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">
            Welcome to <span className="text-gradient">Patch Manager</span>
          </h1>
          <p className="text-xl text-gray-400">
            Hi {user?.firstName || 'there'}! Let's get you set up.
          </p>
        </div>

        <div className="card space-y-6">
          <div>
            <h2 className="text-2xl font-semibold mb-2">Stay Informed</h2>
            <p className="text-gray-400">
              Get the latest updates on software versions and security vulnerabilities delivered to your inbox.
            </p>
          </div>

          <div className="border border-gray-700 rounded-lg p-6 bg-dark-bg-tertiary space-y-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={subscribeToEmails}
                onChange={(e) => setSubscribeToEmails(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-600 bg-dark-bg text-primary-purple focus:ring-2 focus:ring-primary-purple focus:ring-offset-0"
              />
              <div className="flex-1">
                <div className="font-semibold text-lg">Subscribe to Daily Email Digest</div>
                <div className="text-sm text-gray-400 mt-1">
                  Receive a daily summary of:
                </div>
                <ul className="text-sm text-gray-400 mt-2 space-y-1 ml-4">
                  <li>• New software versions released</li>
                  <li>• New CVEs discovered</li>
                  <li>• Affected versions and upgrade recommendations</li>
                </ul>
                <div className="text-xs text-gray-500 mt-3">
                  You can change this preference anytime in your profile settings.
                </div>
              </div>
            </label>
          </div>

          <div className="flex justify-center pt-4">
            <button
              onClick={handleContinue}
              disabled={isLoading}
              className="btn btn-primary px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Setting up...' : 'Continue to Dashboard'}
            </button>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>
            By continuing, you agree to our{' '}
            <a href="/privacy" className="text-primary-purple hover:underline">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/terms" className="text-primary-purple hover:underline">
              Terms of Service
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
